import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { oapi } from '@/lib/officialsApi';

export const PublicShell = ({ children }) => (
  <div className="min-h-screen bg-[#020817] flex items-center justify-center px-4 py-10 relative overflow-hidden">
    <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'repeating-linear-gradient(135deg, #fff 0 1px, transparent 1px 22px)' }} />
    <div className="relative w-full max-w-md">
      <Link to="/" className="block text-center mb-6"><img src="/logo-transparent.png" alt="Schedule Guru" className="h-10 mx-auto brightness-0 invert" /></Link>
      {children}
    </div>
  </div>
);

const Field = ({ label, value, onChange, placeholder, testId }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label><Input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} /></div>
);

const SignupForm = ({ f, set }) => (
  <div className="space-y-3 text-left" data-testid="join-profile-form">
    <div className="grid grid-cols-2 gap-3">
      <Field label="First name" value={f.first_name} onChange={v => set('first_name', v)} testId="join-first-name" />
      <Field label="Last name" value={f.last_name} onChange={v => set('last_name', v)} testId="join-last-name" />
    </div>
    <Field label="Phone" value={f.phone} onChange={v => set('phone', v)} placeholder="+1 345 555 0100" testId="join-phone" />
    <Field label="Sports you officiate" value={f.sports} onChange={v => set('sports', v)} placeholder="basketball, netball" testId="join-sports" />
    <Field label="Certifications (optional)" value={f.certifications} onChange={v => set('certifications', v)} placeholder="FIBA Level 1" testId="join-certifications" />
  </div>
);

export default function OfficialsJoin() {
  const { token } = useParams();
  const { isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ first_name: '', last_name: '', phone: '', sports: '', certifications: '' });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  useEffect(() => { oapi(`/officials/invite/${token}`).then(setInfo).catch(e => setErr(e.message)); }, [token]);
  useEffect(() => {
    if (!user) return;
    const [first, ...rest] = (user.name || '').split(' ');
    setF(p => ({ ...p, first_name: p.first_name || first || '', last_name: p.last_name || rest.join(' ') }));
  }, [user]);

  const here = encodeURIComponent(`/officials/join/${token}`);
  const accept = async () => {
    setBusy(true);
    try {
      const r = await oapi(`/officials/invite/${token}/accept`, { method: 'POST', body: info.type === 'org' ? f : {} });
      toast.success(r.already_member ? `You're already on ${r.org_name}'s officials list` : `You've joined ${r.org_name} as an official`);
      navigate('/officials/portal?tab=availability');
    } catch (e) { toast.error(e.message); setBusy(false); }
  };

  if (err) {
    return (
      <PublicShell>
        <div className="bg-white rounded-3xl p-8 text-center" data-testid="join-error">
          <AlertCircle className="w-10 h-10 mx-auto text-red-500" />
          <p className="mt-3 font-bold text-slate-900">{err}</p>
          <p className="text-sm text-slate-500 mt-1">Ask your league for a fresh link.</p>
        </div>
      </PublicShell>
    );
  }
  if (!info || loading) return <PublicShell><Loader2 className="w-8 h-8 animate-spin text-white mx-auto" /></PublicShell>;
  const o = info.official;
  return (
    <PublicShell>
      <div className="bg-white rounded-3xl p-7 sm:p-8 text-center shadow-2xl" data-testid="join-card">
        <span className="w-14 h-14 mx-auto rounded-2xl bg-[#00247D] text-white flex items-center justify-center"><ShieldCheck className="w-7 h-7" /></span>
        <p className="mt-4 text-xs tracking-[0.2em] uppercase font-bold text-[#CF142B]">Officials invitation</p>
        <h1 className="mt-1 text-3xl font-black uppercase text-[#00247D] leading-none" style={{ fontFamily: 'Barlow Condensed' }} data-testid="join-org-name">{info.org_name}</h1>
        <p className="mt-3 text-sm text-slate-600">
          {o ? `Hi ${o.first_name || 'there'}, you've been added as an official.` : 'Join this league\'s officials list.'} Set your availability and answer game assignments online. It's free.
        </p>
        {o?.joined && <p className="mt-3 text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-2" data-testid="join-already-used">This invite was already accepted. If that was you, just open your portal.</p>}
        {!isAuthenticated ? (
          <div className="mt-6 space-y-2">
            <Link to={`/login?mode=register&redirect=${here}${o?.email ? `&email=${encodeURIComponent(o.email)}` : ''}`}>
              <Button className="w-full h-11 bg-[#00247D] hover:bg-[#001A5C]" data-testid="join-register-btn">Create free account</Button>
            </Link>
            <Link to={`/login?redirect=${here}${o?.email ? `&email=${encodeURIComponent(o.email)}` : ''}`}>
              <Button variant="outline" className="w-full h-11 mt-2" data-testid="join-login-btn">I already have an account</Button>
            </Link>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {info.type === 'org' && <SignupForm f={f} set={set} />}
            <Button onClick={accept} disabled={busy} className="w-full h-11 bg-[#00247D] hover:bg-[#001A5C]" data-testid="join-accept-btn">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}{info.type === 'org' ? 'Join as an official' : 'Accept invitation'}
            </Button>
            <p className="text-xs text-slate-400">Signed in as {user?.email}</p>
          </div>
        )}
      </div>
    </PublicShell>
  );
}
